import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { models } from 'powerbi-client';
import { ReportService } from '../../../services/reports/report.service';
import {
  ReportEmbedEntry,
  buildReportParamsQueryString,
  populateReportConfigMap,
  findReportEntryById,
  getReportParamsFromUrlOrStorage
} from '../report-embed.utils';

/**
 * Displays a single Power BI report by id. No tabs – used when the app is opened
 * with reportId in the URL so the report opens directly.
 */
@Component({
  selector: 'app-report-only',
  templateUrl: './report-only.component.html',
  styleUrls: ['./report-only.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportOnlyComponent implements OnChanges, OnDestroy {
  @Input() reportId: string | null = null;

  reportConfig = {
    type: 'report' as const,
    id: '',
    embedUrl: '',
    accessToken: '',
    tokenType: models.TokenType.Embed,
    hostname: 'https://app.powerbi.com',
    settings: {
      panes: {
        filters: { visible: false },
        pageNavigation: { visible: false }
      }
    }
  };
  showSpinner = true;
  loadError: string | null = null;

  private readonly reportConfigByReportId = new Map<string, ReportEmbedEntry>();
  private loadSubscription: Subscription | null = null;

  constructor(private readonly reportService: ReportService) {}

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const idChange = changes['reportId'];
    if (!idChange || !this.reportId) return;
    // Load only on first bind or when reportId actually changed to avoid duplicate API calls and re-embeds (which abort session requests).
    const first = idChange.firstChange;
    const changed = !first && idChange.previousValue !== idChange.currentValue;
    if (first || changed) {
      this.loadReport(this.reportId);
    }
  }

  private loadReport(reportId: string): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
    this.showSpinner = true;
    this.loadError = null;
    // Same API as Inquiry: getPowerBIMisReport. Pass reportId and rp params so backend can use them; we also append rp:* client-side (Inquiry-style).
    const reportParams = this.getReportParamsFromUrl();
    this.loadSubscription = this.reportService.getPowerBIMisReport(reportId.trim(), reportParams).subscribe({
      next: reports => {
        this.showSpinner = false;
        const results = reports?.results;
        if (results?.length > 0) {
          this.buildReportConfigMap(results);
          const entry = this.findReportEntryById(reportId);
          if (entry) {
            // Inquiry flow: build fullEmbedUrl by appending rp:* and toolbar=false client-side (same as inquiry-reports onRequestReport).
            let embedUrl = entry.embedUrl;
            if (reportParams && Object.keys(reportParams).length > 0) {
              const query = this.buildReportParamsQueryString(reportParams);
              if (query) {
                const joinChar = embedUrl.includes('?') ? '&' : '?';
                embedUrl = embedUrl + joinChar + query + '&toolbar=false';
              }
            }
            this.reportConfig = {
              ...this.reportConfig,
              id: entry.reportId,
              embedUrl,
              accessToken: entry.accessToken
            };
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem('powerbi_reportId');
              sessionStorage.removeItem('powerbi_reportParams');
            }
          } else {
            this.loadError = 'Report not found in workspace.';
          }
        } else {
          this.loadError = 'No reports returned.';
        }
      },
      error: err => {
        this.showSpinner = false;
        this.loadError = err?.message || 'Failed to load report.';
        console.error('ReportOnlyComponent: load error', err);
      }
    });
    this.loadSubscription.add(() => { this.loadSubscription = null; });
  }

  private buildReportConfigMap(results: Array<{ embedReports?: Array<{ reportId?: string; embedUrl?: string; reportName?: string }>; embedToken?: { token?: string } }>): void {
    populateReportConfigMap(this.reportConfigByReportId, results);
  }

  /** Build query string from rp:* params (same format as Inquiry reports). */
  private buildReportParamsQueryString(reportParams: Record<string, string | string[]>): string {
    return buildReportParamsQueryString(reportParams);
  }

  /** Parse current URL and return report parameters (rp:*) for the backend to append to embed URLs. */
  private getReportParamsFromUrl(): Record<string, string | string[]> | undefined {
    return getReportParamsFromUrlOrStorage();
  }

  private findReportEntryById(reportId: string): ReportEmbedEntry | undefined {
    return findReportEntryById(this.reportConfigByReportId, reportId);
  }
}
